/**
 * @fileoverview Zcash Helpers – address handling, validation & bitcoinjs-lib patching
 *
 * Utilities for working with Zcash transparent, shielded, and unified addresses.
 * Includes a `bitcoinjs-lib` monkey-patch so `toOutputScript()` handles Zcash t-addresses.
 *
 * Ported from WINBIT32 zcashHelpers.ts – external dependencies:
 *   @scure/bip39, @scure/bip32, @noble/curves/secp256k1,
 *   bitcoinjs-lib, bs58check.
 *
 * If the consuming project does NOT need `bitcoinjs-lib` patching or PSBT helpers
 * it is safe to import only the address-validation helpers.
 */

import {
	ZCASH_MAINNET_NETWORK,
	ZCASH_DERIVATION_PATH,
	ZCASH_SAPLING_PATH,
} from './constants';
import type { ZcashAddressValidation } from './types';
import { Buffer } from 'buffer';

// ---------------------------------------------------------------------------
// Address prefixes (raw bytes for bs58check encode/decode)
// ---------------------------------------------------------------------------

export const ZcashPrefixes = {
	/** Mainnet transparent P2PKH */
	tMainnet: Buffer.from([0x1c, 0xb8]),
	/** Testnet transparent P2PKH */
	tTestnet: Buffer.from([0x1d, 0x25]),
	/** Mainnet Sapling shielded */
	zSMainnet: Buffer.from([0x16, 0x9a]),
	/** Testnet Sapling shielded */
	zSTestnet: Buffer.from([0x16, 0xb6]),
} as const;

// ---------------------------------------------------------------------------
// Address validation
// ---------------------------------------------------------------------------

/**
 * Validate a Zcash address and determine its type.
 *
 * Supports transparent (`t1…`), Sapling (`zs1…`), and unified (`u1…`) addresses.
 *
 * The function requires `bs58check` at runtime for transparent/shielded decoding.
 * Unified address validation is prefix+length only (full F4Jumble check is left
 * to the WASM library when actually spending).
 */
export const isValidZcashAddress = (
	address: string,
): { isValid: boolean; type: 'transparent' | 'shielded' | 'unified' | 'invalid' } => {
	try {
		// Unified address (u1…)
		if (address.startsWith('u1')) {
			// ZIP-316: T+S ≈ 116 chars, T+S+O ≈ 213 chars; allow 50-300
			if (address.length >= 50 && address.length <= 300) {
				return { isValid: true, type: 'unified' };
			}
			return { isValid: false, type: 'invalid' };
		}

		// Transparent (t1… / t3…)
		if (address.startsWith('t')) {
			let bs58check: any;
			try {
				// eslint-disable-next-line @typescript-eslint/no-var-requires
				bs58check = require('bs58check');
			} catch {
				// If bs58check isn't available, do a loose check
				return address.length >= 25 && address.length <= 36
					? { isValid: true, type: 'transparent' }
					: { isValid: false, type: 'invalid' };
			}
			const decoded: Buffer = bs58check.decode(address);
			const prefix = decoded.length >= 2 ? decoded.slice(0, 2) : Buffer.alloc(2);
			if (
				Buffer.compare(prefix, ZcashPrefixes.tMainnet) === 0 ||
				Buffer.compare(prefix, ZcashPrefixes.tTestnet) === 0
			) {
				return { isValid: true, type: 'transparent' };
			}
		}

		// Sapling shielded (zs1… / ztestsapling…)
		if (address.startsWith('z')) {
			let bs58check: any;
			try {
				// eslint-disable-next-line @typescript-eslint/no-var-requires
				bs58check = require('bs58check');
			} catch {
				return address.length > 40
					? { isValid: true, type: 'shielded' }
					: { isValid: false, type: 'invalid' };
			}
			const decoded: Buffer = bs58check.decode(address);
			const prefix = decoded.length >= 2 ? decoded.slice(0, 2) : Buffer.alloc(2);
			if (
				Buffer.compare(prefix, ZcashPrefixes.zSMainnet) === 0 ||
				Buffer.compare(prefix, ZcashPrefixes.zSTestnet) === 0
			) {
				return { isValid: true, type: 'shielded' };
			}
		}

		return { isValid: false, type: 'invalid' };
	} catch {
		return { isValid: false, type: 'invalid' };
	}
};

// ---------------------------------------------------------------------------
// Output script helpers
// ---------------------------------------------------------------------------

/**
 * Convert a transparent Zcash address to a P2PKH output script.
 *
 * For shielded/unified addresses returns a 1-byte placeholder (`OP_0`).
 * Real shielded sends use the PCZT / z_sendmany flow.
 */
export const toZcashOutputScript = (address: string): Buffer => {
	const validation = isValidZcashAddress(address);
	if (!validation.isValid) {
		throw new Error(`Invalid Zcash address: ${address}`);
	}

	if (validation.type === 'transparent') {
		let bs58check: any;
		let bitcoinScript: any;
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			bs58check = require('bs58check');
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const bjsLib = require('bitcoinjs-lib');
			bitcoinScript = bjsLib.script;
		} catch {
			throw new Error('bs58check and bitcoinjs-lib are required for toZcashOutputScript');
		}

		const decoded: Buffer = bs58check.decode(address);
		const pubKeyHash = decoded.length > 2 ? decoded.slice(2) : Buffer.alloc(20);

		// OP_DUP OP_HASH160 <pubKeyHash> OP_EQUALVERIFY OP_CHECKSIG
		return bitcoinScript.compile([
			bitcoinScript.OPS.OP_DUP,
			bitcoinScript.OPS.OP_HASH160,
			Buffer.from(pubKeyHash),
			bitcoinScript.OPS.OP_EQUALVERIFY,
			bitcoinScript.OPS.OP_CHECKSIG,
		]);
	}

	// Shielded / unified → placeholder
	return Buffer.from([0x00]);
};

// ---------------------------------------------------------------------------
// bitcoinjs-lib patching
// ---------------------------------------------------------------------------

/**
 * Patch `bitcoinjs-lib`'s `address.toOutputScript()` so it handles
 * Zcash transparent addresses (`t1…`) that would otherwise throw.
 *
 * Safe to call multiple times – subsequent calls are no-ops.
 */
export const patchBitcoinJsLibForZcash = (): void => {
	let bitcoinAddress: any;
	try {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const bjsLib = require('bitcoinjs-lib');
		bitcoinAddress = bjsLib.address;
	} catch {
		// bitcoinjs-lib not available – nothing to patch
		return;
	}

	if (bitcoinAddress.__zcashPatched) return;

	const originalToOutputScript = bitcoinAddress.toOutputScript;

	bitcoinAddress.toOutputScript = (address: string, network?: any): Buffer => {
		try {
			if (
				address &&
				typeof address === 'string' &&
				(address.startsWith('t') || address.startsWith('z') || address.startsWith('u1'))
			) {
				const validation = isValidZcashAddress(address);
				if (validation.isValid) {
					return toZcashOutputScript(address);
				}
			}
			return originalToOutputScript(address, network);
		} catch (error) {
			// Fallback: try Zcash handler when original throws
			if (
				address &&
				typeof address === 'string' &&
				(address.startsWith('t') || address.startsWith('z') || address.startsWith('u1'))
			) {
				try {
					return toZcashOutputScript(address);
				} catch {
					/* rethrow original */
				}
			}
			throw error;
		}
	};

	bitcoinAddress.__zcashPatched = true;
	console.log('Bitcoin.js library successfully patched to handle Zcash addresses');
};

// ---------------------------------------------------------------------------
// Transparent key derivation helpers
// ---------------------------------------------------------------------------

/**
 * Create a Zcash mainnet transparent (t1…) address from a compressed public key.
 */
export const createZcashAddress = (publicKey: Buffer): string => {
	let bs58check: any;
	let bjsCrypto: any;
	try {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		bs58check = require('bs58check');
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		bjsCrypto = require('bitcoinjs-lib').crypto;
	} catch {
		throw new Error('bs58check and bitcoinjs-lib are required for createZcashAddress');
	}

	const pubKeyHash = bjsCrypto.hash160(publicKey);
	const addressBytes = Buffer.concat([ZcashPrefixes.tMainnet, pubKeyHash]);
	return bs58check.encode(addressBytes);
};

// ---------------------------------------------------------------------------
// Extended chain / derivation-path helpers
// ---------------------------------------------------------------------------

/**
 * Extended derivation paths that include Zcash.
 *
 * Consumers that use `@doritokit/helpers` can merge these into their own
 * DerivationPath objects.
 */
export const ZcashDerivationPaths = {
	/** BIP-44 transparent (coin type 133) */
	transparent: ZCASH_DERIVATION_PATH,
	/** ZIP-32 shielded */
	shielded: ZCASH_SAPLING_PATH,
} as const;

/**
 * Zcash mainnet network object (for bitcoinjs-lib).
 *
 * Note: pubKeyHash 0x1cb8 and scriptHash 0x1cbd exceed UInt8 — bitcoinjs-lib's
 * `address.fromBase58Check` won't decode Zcash addresses unless the library
 * has been patched (see `patchBitcoinJsLibForZcash`).
 */
export const zcashNetwork = ZCASH_MAINNET_NETWORK;

// ---------------------------------------------------------------------------
// PSBT helpers (transparent Zcash transactions)
// ---------------------------------------------------------------------------

/** Check if a raw buffer looks like a Zcash (overwintered) transaction. */
export const isValidZcashTransaction = (buffer: Buffer): boolean => {
	if (buffer.length < 8) return false;
	const version = buffer.readUInt32LE(0);
	return (version & 0x80000000) !== 0;
};

/**
 * Prepare a PSBT for Zcash signing.
 *
 * For transparent addresses the format is identical to Bitcoin — no special
 * handling beyond ensuring address prefixes are correct.
 */
export const prepareZcashPsbtForSigning = (psbt: any, _inputs: any[]): any => {
	return psbt;
};

/**
 * Convert witnessUtxo → nonWitnessUtxo by fetching the raw transaction.
 */
export const convertWitnessUtxoToNonWitnessUtxo = async (
	input: any,
	apiClient: any,
): Promise<void> => {
	if (input.nonWitnessUtxo || !input.witnessUtxo) return;

	if (input.txHex) {
		input.nonWitnessUtxo = Buffer.from(input.txHex, 'hex');
		delete input.witnessUtxo;
		return;
	}

	if (input.txid) {
		try {
			const txid = input.txid.toString('hex');
			const txHex = await apiClient.getRawTx(txid);
			if (txHex) {
				input.nonWitnessUtxo = Buffer.from(txHex, 'hex');
				delete input.witnessUtxo;
			}
		} catch {
			// continue with witnessUtxo
		}
	}
};

/**
 * Process all inputs in a Zcash PSBT to handle overwintered-transaction
 * buffers that would break `bitcoinjs-lib` parsing.
 */
export const processZcashPsbtInputs = async (
	psbt: any,
	apiClient: any,
): Promise<void> => {
	if (!psbt.data.inputs || psbt.data.inputs.length === 0) return;

	for (let i = 0; i < psbt.data.inputs.length; i++) {
		const input = psbt.data.inputs[i];

		if (input._zcashTxHex) {
			try {
				const zcashTxBuffer = Buffer.from(input._zcashTxHex, 'hex');
				if (isValidZcashTransaction(zcashTxBuffer)) {
					input._zcashTxBuffer = zcashTxBuffer;
					input._isZcashInput = true;
					delete input.nonWitnessUtxo;
				}
			} catch {
				/* ignore */
			}
		} else if (input.nonWitnessUtxo && isValidZcashTransaction(input.nonWitnessUtxo)) {
			input._zcashTxBuffer = input.nonWitnessUtxo;
			delete input.nonWitnessUtxo;
			input._isZcashInput = true;
		} else if (input.witnessUtxo && !input.nonWitnessUtxo && !input._isZcashInput) {
			await convertWitnessUtxoToNonWitnessUtxo(input, apiClient);
		}
	}
};

